// ═══════════════════════════════════════════════════════════════════════════
// DISTIL PROTOCOL - BLOCKVISION API
// Single API call returns wallet coins + prices + metadata instantly.
// Replaces getCoinMetadata RPC calls entirely — no CORS issues.
// ═══════════════════════════════════════════════════════════════════════════

const BV_BASE_URL = 'https://api.blockvision.org/v2/sui';
const CACHE_DURATION = 30000; // 30 seconds

// ── API Key Management ───────────────────────────────────────────────────────
let _apiKey = null;

export function setBlockVisionApiKey(key) {
  _apiKey = key;
}

export function getBlockVisionApiKey() {
  if (_apiKey) return _apiKey;
  try {
    const stored = localStorage.getItem('distil_bv_api_key');
    if (stored) {
      _apiKey = stored;
      return _apiKey;
    }
  } catch (e) {}
  return import.meta.env.VITE_BLOCKVISION_API_KEY || null;
}

// ── RPC URL Management ───────────────────────────────────────────────────────
// Private BlockVision RPC — CORS-friendly, higher rate limits than public endpoint
const DEFAULT_RPC_URL = 'https://sui-mainnet-endpoint.blockvision.org';
let _rpcUrl = null;

export function setBlockVisionRpcUrl(url) {
  _rpcUrl = url;
}

export function getBlockVisionRpcUrl() {
  if (_rpcUrl) return _rpcUrl;
  try {
    const stored = localStorage.getItem('distil_bv_rpc_url');
    if (stored) {
      _rpcUrl = stored;
      return _rpcUrl;
    }
  } catch (e) {}
  return import.meta.env.VITE_BLOCKVISION_RPC_URL || DEFAULT_RPC_URL;
}

// ── Cache ────────────────────────────────────────────────────────────────────
const cache = {
  priceMap: {},      // { coinType: usdPrice }
  metadataMap: {},   // { coinType: { name, symbol, decimals, logo } }
  timestamp: null,
  address: null,
};

function isCacheValid(address) {
  return (
    cache.address === address &&
    cache.timestamp &&
    Date.now() - cache.timestamp < CACHE_DURATION &&
    Object.keys(cache.priceMap).length > 0
  );
}

// ── Core Fetch ───────────────────────────────────────────────────────────────
/**
 * Fetches all coins + prices + metadata for a wallet in ONE API call.
 * Returns priceMap and metadataMap — no separate RPC calls needed.
 */
export async function fetchWalletCoinsWithPrices(address) {
  if (!address) return { priceMap: {}, metadataMap: {}, coinData: [] };

  if (isCacheValid(address)) {
    console.log('💰 [BV] Using cached data');
    return { priceMap: cache.priceMap, metadataMap: cache.metadataMap, coinData: [] };
  }

  const apiKey = getBlockVisionApiKey();
  if (!apiKey) {
    console.warn('⚠️ [BV] No BlockVision API key set — prices unavailable');
    return { priceMap: {}, metadataMap: {}, coinData: [] };
  }

  try {
    console.log('📡 [BV] Fetching wallet coins + prices + metadata...');

    const response = await fetch(
      `${BV_BASE_URL}/account/coins?account=${address}`,
      {
        headers: {
          'x-api-key': apiKey,
          'Accept': 'application/json',
        },
      }
    );

    if (!response.ok) {
      console.error(`❌ [BV] API error: ${response.status}`);
      return { priceMap: {}, metadataMap: {}, coinData: [] };
    }

    const data = await response.json();
    const coins = data.result?.data || [];

    const priceMap = {};
    const metadataMap = {};

    coins.forEach(coin => {
      if (!coin.coinType) return;

      // Price map
      const price = parseFloat(coin.price);
      if (!isNaN(price) && price > 0) {
        priceMap[coin.coinType] = price;
      }

      // Metadata map — replaces getCoinMetadata RPC calls
      metadataMap[coin.coinType] = {
        name: coin.name || coin.symbol || coin.coinType.split('::').pop(),
        symbol: coin.symbol || coin.coinType.split('::').pop(),
        decimals: typeof coin.decimals === 'number' ? coin.decimals : 9,
        iconUrl: coin.logo || null,
        verified: coin.verified || false,
      };
    });

    // Update cache
    cache.priceMap = priceMap;
    cache.metadataMap = metadataMap;
    cache.timestamp = Date.now();
    cache.address = address;

    console.log(`✅ [BV] Got ${Object.keys(priceMap).length} prices, ${Object.keys(metadataMap).length} metadata entries`);
    return { priceMap, metadataMap, coinData: coins };

  } catch (error) {
    console.error('❌ [BV] Fetch error:', error.message);
    return { priceMap: {}, metadataMap: {}, coinData: [] };
  }
}

/**
 * Get cached metadata for a coin type.
 * Use this in coinMetadata.js instead of suiClient.getCoinMetadata()
 */
export function getCachedMetadata(coinType) {
  return cache.metadataMap[coinType] || null;
}

/**
 * Get cached price for a coin type.
 */
export function getCachedPrice(coinType) {
  return cache.priceMap[coinType] || null;
}

/**
 * Clear cache (call after purification)
 */
export function clearBlockVisionCache() {
  cache.priceMap = {};
  cache.metadataMap = {};
  cache.timestamp = null;
  cache.address = null;
  console.log('🗑️ [BV] Cache cleared');
}

export default {
  fetchWalletCoinsWithPrices,
  getCachedMetadata,
  getCachedPrice,
  clearBlockVisionCache,
  setBlockVisionApiKey,
  getBlockVisionApiKey,
};
